import { createSlice } from "@reduxjs/toolkit";
import { CsvReportNameEnum, CsvJobNameKeyEnum } from "./enums";
import { csvReportsApi } from "./endpoints";
import { TeamUsersCsvT, OrgProjectsCsvT } from "./types";

export type CsvReportsReduxStateT = {
  [CsvReportNameEnum.TEAM_USERS]: TeamUsersCsvT;
  [CsvReportNameEnum.ORG_TEAM_USERS]: TeamUsersCsvT;
  [CsvReportNameEnum.ORG_PRE_SALES_PROJECTS]: OrgProjectsCsvT;
  [CsvReportNameEnum.ORG_POST_SALES_PROJECTS]: OrgProjectsCsvT;
  [CsvReportNameEnum.ORG_COMPLETED_PROJECTS]: OrgProjectsCsvT;
};

const csvJobInitialState = <T>(jobTitle: CsvJobNameKeyEnum, reportName: CsvReportNameEnum): T => {
  return {
    createdAt: "",
    id: 0,
    jobName: null,
    jobStatus: null,
    jobs: {
      [jobTitle]: {
        results: false,
        state: null,
        ts: "",
      },
    },
    name: reportName,
    url: null,
    updatedAt: "",
    userId: 0,
  } as T;
};

const initialState: CsvReportsReduxStateT = {
  [CsvReportNameEnum.TEAM_USERS]: csvJobInitialState<TeamUsersCsvT>(
    CsvJobNameKeyEnum.TEAM_USERS,
    CsvReportNameEnum.TEAM_USERS,
  ),
  [CsvReportNameEnum.ORG_TEAM_USERS]: csvJobInitialState<TeamUsersCsvT>(
    CsvJobNameKeyEnum.TEAM_USERS,
    CsvReportNameEnum.ORG_TEAM_USERS,
  ),
  [CsvReportNameEnum.ORG_PRE_SALES_PROJECTS]: csvJobInitialState<OrgProjectsCsvT>(
    CsvJobNameKeyEnum.PROJECTS,
    CsvReportNameEnum.ORG_PRE_SALES_PROJECTS,
  ),
  [CsvReportNameEnum.ORG_POST_SALES_PROJECTS]: csvJobInitialState<OrgProjectsCsvT>(
    CsvJobNameKeyEnum.PROJECTS,
    CsvReportNameEnum.ORG_POST_SALES_PROJECTS,
  ),
  [CsvReportNameEnum.ORG_COMPLETED_PROJECTS]: csvJobInitialState<OrgProjectsCsvT>(
    CsvJobNameKeyEnum.PROJECTS,
    CsvReportNameEnum.ORG_COMPLETED_PROJECTS,
  ),
};

export const csvReportsSlice = createSlice({
  name: "csvReports",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addMatcher(csvReportsApi.endpoints?.getTeamUsersCsv.matchFulfilled, (state, { payload }) => {
      state[payload.csv.name] = payload.csv;
    });
    builder.addMatcher(csvReportsApi.endpoints?.getOrgProjectsCsv.matchFulfilled, (state, { payload }) => {
      state[payload.csv.name] = payload.csv;
    });
    builder.addMatcher(csvReportsApi.endpoints?.getCsvReportById.matchFulfilled, (state, { payload }) => {
      if (
        payload.report.name &&
        [...Object.values(CsvReportNameEnum)].includes(payload.report.name as CsvReportNameEnum)
      ) {
        state[payload.report.name] = { ...state[payload.report.name], ...payload.report };
      }
    });
  },
  selectors: {
    selectTeamUsersCsv: (state) => state[CsvReportNameEnum.TEAM_USERS],
    selectOrgTeamUsersCsv: (state) => state[CsvReportNameEnum.ORG_TEAM_USERS],
    selectOrgPreSalesCsv: (state) => state[CsvReportNameEnum.ORG_PRE_SALES_PROJECTS],
    selectOrgPostSalesCsv: (state) => state[CsvReportNameEnum.ORG_POST_SALES_PROJECTS],
    selectOrgCompletedCsv: (state) => state[CsvReportNameEnum.ORG_COMPLETED_PROJECTS],
  },
});

export const {
  selectTeamUsersCsv,
  selectOrgTeamUsersCsv,
  selectOrgPreSalesCsv,
  selectOrgPostSalesCsv,
  selectOrgCompletedCsv,
} = csvReportsSlice.selectors;

export default csvReportsSlice;
